import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { withRouter } from 'react-router'

import { AnimatePresence } from 'framer-motion'

import { useEditor } from '@toasttab/sites-components'
import { t } from 'src/translations'

import { CtaType } from 'src/apollo/sites'
import { RequestContextProps } from 'src/lib/js/context'
import useTracker from 'src/lib/js/hooks/useTracker'
import { TrackingEvents } from 'shared/components/common/tracking/TrackingEvents'
import { isToastLocalRequest } from 'src/public/js/siteUtilities'

import {
  Modal,
  ModalOverlay,
  useModal,
  ModalContent
} from 'shared/components/common/modal'
import {
  useOptionalRestaurant,
  useRestaurant
} from 'shared/components/common/restaurant_context/RestaurantContext'
import {
  useRestaurantRoutes,
  isValidAccountPath
} from 'shared/components/common/restaurant_routes/RestaurantRoutesContext'
import { alertSuccess } from 'shared/js/alertUtils'

import { CTAData } from 'public/components/default_template/ctas'
import { DEFAULT_COLORS } from 'public/components/default_template/meta/StyleMeta'
import { useThemeColorScheme } from 'public/components/default_template/meta/useTheme'
import { MobileNavItems } from 'public/components/default_template/nav/NavItems'
import { Logout } from 'public/components/default_template/online_ordering/account/logout/Logout'
import { LogoutConfirmation } from 'public/components/default_template/online_ordering/account/logout/LogoutConfirmation'
import { PwlessAuth } from 'public/components/default_template/online_ordering/account/pwlessAuth/PwlessAuth'
import { AuthenticationSource } from 'public/components/default_template/online_ordering/checkout/checkoutUtils'
import { useCustomer } from 'public/components/online_ordering/CustomerContextCommon'

import AccountDropdown from './AccountDropdown'
import LogoutIcon from './LogoutIcon'
import UserIcon from './UserIcon'
import UserAvatar from './UserAvatar'
import { getNavColorStyles } from './navUtils'

const accountCtas = (accountPath: string, onClick: () => void): CTAData[] => {
  const ctas: CTAData[] = []

  if (isValidAccountPath(accountPath)) {
    ctas.push({
      nav: true,
      text: t('user-nav.my-account'),
      type: CtaType.Link,
      link: accountPath,
      icon: <UserIcon color={'currentColor'} />
    })
  }

  // Always include logout
  ctas.push({
    nav: true,
    text: t('user-nav.log-out'),
    type: 'customAction',
    onClick: onClick,
    icon: <LogoutIcon color={'currentColor'} />
  })

  return ctas
}

type MobileUserNavItemsProps = {
  onOpen: () => void
  onClose: () => void
  setModalContent: (content: JSX.Element) => void
  closeOverlay: () => void
  color?: string | null
}

export const MobileUserNavItems = ({
  onOpen,
  onClose,
  setModalContent,
  closeOverlay,
  color
}: MobileUserNavItemsProps) => {
  const { customer, pwlessLogout } = useCustomer()
  const restaurant = useOptionalRestaurant()?.restaurant
  const { accountPath } = useRestaurantRoutes()
  const modalContentStyle = getNavColorStyles(restaurant, false)

  const openModal = useCallback(
    (content: JSX.Element) => () => {
      setModalContent(content)
      onOpen()
    },
    [onOpen, setModalContent]
  )

  const logout = useCallback(async () => {
    openModal(<Logout />)()
    const success = await pwlessLogout()
    if (success) {
      onClose()
      alertSuccess(t('user-nav.now-logged-out'))
    } else {
      setModalContent(<Logout error />)
    }
  }, [openModal, onClose, setModalContent, pwlessLogout])

  const customerCtas = useMemo(
    (): CTAData[] => accountCtas(accountPath, logout),
    [accountPath, logout]
  )

  const loginCtas = useMemo(
    (): CTAData[] =>
      restaurant?.content?.navConfig?.showToastLogin
        ? [
            {
              nav: true,
              text: t('user-nav.log-in-sign-up'),
              type: 'customAction',
              onClick: openModal(
                <PwlessAuth source={AuthenticationSource.UserNav} />
              ),
              icon: <UserIcon color={'currentColor'} />
            }
          ]
        : [],
    [openModal, restaurant?.content?.navConfig?.showToastLogin]
  )

  return (
    <MobileNavItems
      ctas={customer ? customerCtas : loginCtas}
      closeOverlay={closeOverlay}
      color={color || modalContentStyle.color}
    />
  )
}

const UserNav = ({
  staticContext
}: Pick<RequestContextProps, 'staticContext'>) => {
  const { customer, pwlessLogout } = useCustomer()
  const { accountPath } = useRestaurantRoutes()
  const { isOpen, onClose, onOpen } = useModal()
  const {
    isOpen: isLogoutOpen,
    onClose: logoutOnClose,
    onOpen: logoutOnOpen
  } = useModal()
  const {
    isOpen: isLogoutConfirmationOpen,
    onClose: logoutConfirmationOnClose,
    onOpen: logoutConfirmationOnOpen
  } = useModal()
  const [showAccountModal, setShowAccountModal] = useState(false)
  const tracker = useTracker()
  const isToastLocal = isToastLocalRequest(staticContext)

  const [logoutError, setLogoutError] = useState(false)
  const accountButtonPosition = useRef<HTMLButtonElement>(null)
  const [isMobile, setIsMobile] = useState(false)
  const [modalPosition, setModalPosition] = useState<{
    top: string
    right: string
  }>()
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Positions desktop modal relative to account button
  const calculateModalPosition = useCallback(() => {
    if (isMobile) return undefined

    const buttonPosition =
      accountButtonPosition.current?.getBoundingClientRect()

    if (!buttonPosition) return undefined

    return {
      top: `${buttonPosition.bottom + 8}px`,
      right: `${window.innerWidth - buttonPosition.right}px`
    }
  }, [isMobile])

  useEffect(() => {
    if (!showAccountModal || isMobile) {
      setModalPosition(undefined)
      return undefined
    }

    const updatePosition = () => {
      const position = calculateModalPosition()
      if (position) {
        setModalPosition(position)
      }
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)

    return () => {
      window.removeEventListener('resize', updatePosition)
    }
  }, [showAccountModal, isMobile, calculateModalPosition])

  const handleAccountClick = useCallback(() => {
    const position = calculateModalPosition()
    if (position) {
      setModalPosition(position)
    }
    setShowAccountModal(true)
  }, [calculateModalPosition])

  const handleLogoutClick = useCallback(() => {
    setShowAccountModal(false)
    logoutConfirmationOnOpen()
  }, [logoutConfirmationOnOpen])

  const confirmLogout = useCallback(async () => {
    logoutConfirmationOnClose()
    logoutOnOpen()
    const success = await pwlessLogout()
    if (success) {
      logoutOnClose()
      alertSuccess(t('user-nav.now-logged-out'))
    } else {
      setLogoutError(true)
    }
  }, [logoutConfirmationOnClose, logoutOnOpen, logoutOnClose, pwlessLogout])

  if (!customer) {
    return (
      <>
        <button
          className={isToastLocal ? 'userNavLoginBtn' : 'userNavSignInBtn'}
          data-testid='userNav'
          tabIndex={0}
          aria-label={t('auth-tooltip.sign-in')}
          aria-haspopup='dialog'
          onClick={() => {
            onOpen()
            if (
              typeof window !== 'undefined' &&
              typeof document !== 'undefined'
            ) {
              tracker.track(TrackingEvents.STARTED_AUTHENTICATION, {
                source: AuthenticationSource.UserNav,
                isAuthTooltipVisible: document.querySelector('.authTooltip')
                  ? true
                  : false
              })
            } else {
              tracker.track(TrackingEvents.STARTED_AUTHENTICATION, {
                source: AuthenticationSource.UserNav,
                isAuthTooltipVisible: false
              })
            }
          }}
        >
          {t('user-nav.sign-in')}
        </button>
        <Modal isOpen={isOpen} onClose={onClose}>
          <ModalOverlay fadeIn fadeOut />
          <PwlessAuth source={AuthenticationSource.UserNav} />
        </Modal>
      </>
    )
  }

  return (
    <>
      <button
        className='userNav'
        data-testid='userNav'
        aria-label={t('user-nav.account-actions')}
        onClick={handleAccountClick}
        ref={accountButtonPosition}
      >
        <UserAvatar
          firstName={customer.firstName}
          lastName={customer.lastName}
          useThemeColors
        />
      </button>
      <AnimatePresence>
        <Modal
          isOpen={showAccountModal}
          onClose={() => setShowAccountModal(false)}
          animateClose={isMobile}
        >
          <ModalOverlay
            fadeIn={isMobile}
            fadeOut={isMobile}
            opacity={isMobile ? 60 : 0}
            color={isMobile ? undefined : 'transparent'}
            duration={isMobile ? 0.225 : 0}
          />
          <ModalContent
            contentClassName={
              isMobile
                ? 'accountDropdownMobileContent fullScreenMobile'
                : 'accountDropdownDesktopContent'
            }
            slideIn={false}
            style={isMobile ? undefined : modalPosition}
          >
            <AccountDropdown
              accountPath={accountPath}
              onLogout={handleLogoutClick}
              showMobileHeader={isMobile}
              onClose={() => setShowAccountModal(false)}
            />
          </ModalContent>
        </Modal>
      </AnimatePresence>
      <Modal
        isOpen={isLogoutConfirmationOpen}
        onClose={logoutConfirmationOnClose}
      >
        <ModalOverlay fadeIn fadeOut />
        <LogoutConfirmation
          onConfirm={confirmLogout}
          onCancel={logoutConfirmationOnClose}
          isMobile={isMobile}
        />
      </Modal>
      <Modal isOpen={isLogoutOpen} onClose={logoutOnClose}>
        <ModalOverlay fadeIn fadeOut />
        <Logout error={logoutError} isMobile={isMobile} />
      </Modal>
    </>
  )
}

const EditorUserNav = () => {
  const { restaurant } = useRestaurant()
  const getColorFromTheme = useThemeColorScheme()
  const iconColor = getColorFromTheme((theme) => {
    return (
      theme.colorOverrides?.navigation?.text ?? theme.colorScheme.icon.default
    )
  }, restaurant?.content?.navConfig?.textColor || restaurant?.meta?.textColor || DEFAULT_COLORS.text)
  return (
    <div className='userNav'>
      <UserIcon color={iconColor} />
    </div>
  )
}

type UserNavProps = {
  forceDisplayToastLogin?: boolean | false
}

const UserNavWithRouter = withRouter<
  UserNavProps & RequestContextProps,
  React.ComponentType<UserNavProps & RequestContextProps>
>(UserNav)

const UserNavWrapper = (props: UserNavProps) => {
  const { isEditor } = useEditor()
  const restaurant = useOptionalRestaurant()?.restaurant

  const navConfig = restaurant?.content?.navConfig
  const shouldShowToastLogin = navConfig?.showToastLogin ?? true

  if (!shouldShowToastLogin && !props.forceDisplayToastLogin) {
    return <></>
  }
  return isEditor ? <EditorUserNav /> : <UserNavWithRouter />
}

export default UserNavWrapper
